// BLOCKED JOBS ***********************************************************************************
// pathname: /v1/blocked-jobs
// ************************************************************************************************
import {
  useSwrInfiniteApi,
  ApiJobListCards,
} from '@hooks/useSwrApi';


// Interface
// ********************************************
interface BlockedJobsInfiniteSwrProps {
  locale?: string;
  shouldFetch?: boolean;
  serverSideData?: ApiJobListCards;
  startIndex?: number;
  limit?: number;
  restUrlParams?: string;
}


/**
 * @description SWR: fetch blocked jobs list with infinite pagination.
 *
 * * **locale**: string. Specifies the locale to use for the request.
 * * **shouldFetch**: boolean. A flag indicating whether to initiate the fetching of blocked jobs.
 * * **serverSideData**: ApiJobListCards, optional. Data obtained from a server-side request, if available. Default undefined.
 * * **startIndex**: number, optional. The initial offset to start fetching from (ie: start fetching from offset 24). Modifies the *offset* url parameter (&offset=0). Default 0.
 * * **limit**: number, optional. The number of job cards to retrieve per call. Modifies the *limit* url parameter (&limit=24). Default 24.
 * * **restUrlParams**: string, optional. Additional URL parameters to add (ie: &sort=relevant'). Default empty string.
 */
export const useSwrBlockedJobsInfinite = (props: BlockedJobsInfiniteSwrProps) => {
  // Props destructuring
  // ********************************************
  const {
    locale,
    shouldFetch = true,
    serverSideData,
    startIndex = 0,
    limit = 24,
    restUrlParams = '',
  } = props;


  // Get data from API
  // ********************************************
  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  } = useSwrInfiniteApi<ApiSchema<'JobResult'>>({
    url: '/v1/blocked-jobs',
    shouldFetch,
    locale,
    urlParams: restUrlParams,
    serverSideData,
    infiniteConfig: {
      paginationType: 'offset',
      limit,
      // the key where the array of items is located in the response
      responseArrayKey: 'items',
      startIndex,
    },
    apiOptions: {
      revalidateOnFocus: false,
      dedupingInterval: 1000 * 60, // 1 minute
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });


  // Return data
  // ********************************************
  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  };
};
